import { useCallback, useState, useEffect } from "react";

const sharedStore = {
  scores: new Map(),
  events: new Map(),
  odds: new Map(),
  lastUpdate: null,
};

// Update rerender
const updateCounters = new Map();
const subscribersByMatchId = new Map();
const lastNotifyByMatchId = new Map();
const MAX_EVENTS = 50;

// Helper functions
const updateScore = (normalizedId, liveScoreToday) => {
  if (liveScoreToday) {
    sharedStore.scores.set(normalizedId, liveScoreToday);
  }
};

const updateEvents = (normalizedId, liveEvent) => {
  if (liveEvent && liveEvent.length > 0) {
    const currentEvents = sharedStore.events.get(normalizedId) || [];
    const newEvents = [...currentEvents, ...liveEvent];

    const trimmedEvents =
      newEvents.length > MAX_EVENTS ? newEvents.slice(-MAX_EVENTS) : newEvents;
    sharedStore.events.set(normalizedId, trimmedEvents);
  }
};

// Update odds
const updateOdds = (normalizedId, liveOdds) => {
  if (liveOdds) {
    sharedStore.odds.set(normalizedId, liveOdds);
  }
};

const updateCounter = (normalizedId) => {
  const currentCount = updateCounters.get(normalizedId) || 0;
  updateCounters.set(normalizedId, currentCount + 1);
};

// Format matchId
const normalizeMatchId = (matchId) => {
  if (matchId == null) return null;
  return String(matchId);
};

// Push Noti
const notifySubscribers = (matchId) => {
  const normalizedId = normalizeMatchId(matchId);
  const lastNotify = lastNotifyByMatchId.get(normalizedId) || 0;
  if (Date.now() - lastNotify < 100) return;
  lastNotifyByMatchId.set(normalizedId, Date.now());

  const subscribers = subscribersByMatchId.get(normalizedId);

  if (subscribers) {
    subscribers.forEach((forceUpdate) => {
      forceUpdate();
    });
  }
};

export function useLiveData(onUpdate) {
  const updateAllMatchData = useCallback(
    (matchId, matchData) => {
      const normalizedId = normalizeMatchId(matchId);
      const { liveScoreToday, liveEvent } = matchData;

      // Update data
      updateScore(normalizedId, liveScoreToday);
      updateEvents(normalizedId, liveEvent);

      // Update timestamp
      sharedStore.lastUpdate = Date.now();
      updateCounter(normalizedId);

      // Notify subscribers
      notifySubscribers(normalizedId);
      // update data
      onUpdate?.("all", matchId, matchData);
    },
    [onUpdate],
  );

  const updateMatchOdds = useCallback(
    (matchId, oddsData) => {
      const normalizedId = normalizeMatchId(matchId);

      // Update odds
      let finalOdds = oddsData;
      if (
        oddsData &&
        typeof oddsData === "object" &&
        !Array.isArray(oddsData) &&
        oddsData[matchId] &&
        !oddsData.data
      ) {
        finalOdds = oddsData[matchId];
      }

      updateOdds(normalizedId, finalOdds);

      // Update timestamp
      sharedStore.lastUpdate = Date.now();
      updateCounter(normalizedId);

      // Notify subscribers
      notifySubscribers(normalizedId);
      // update data
      onUpdate?.("odds", matchId, { liveOdds: finalOdds });
    },
    [onUpdate],
  );

  const clearAllData = useCallback(() => {
    sharedStore.scores.clear();
    sharedStore.events.clear();
    sharedStore.odds.clear();
    sharedStore.lastUpdate = null;
    updateCounters.clear();
    subscribersByMatchId.clear();
  }, []);

  return {
    updateAllMatchData,
    updateMatchOdds,
    clearAllData,
  };
}

export function useLiveDataForMatch(matchId) {
  const normalizedId = normalizeMatchId(matchId);

  const [, setUpdateCounter] = useState(0);

  useEffect(() => {
    const forceUpdate = () => {
      setUpdateCounter((prev) => prev + 1);
    };

    if (!subscribersByMatchId.has(normalizedId)) {
      subscribersByMatchId.set(normalizedId, new Set());
    }

    subscribersByMatchId.get(normalizedId).add(forceUpdate);

    // Cleanup: unsubscribe when unmount
    return () => {
      const subscribers = subscribersByMatchId.get(normalizedId);
      if (subscribers) {
        subscribers.delete(forceUpdate);

        if (subscribers.size === 0) {
          subscribersByMatchId.delete(normalizedId);
        }
      }
    };
  }, [normalizedId]); // Re-subscribe when matchId change

  // Update count
  const updateCounter = updateCounters.get(normalizedId) || 0;

  const getScore = useCallback(() => {
    return sharedStore.scores.get(normalizedId);
  }, [normalizedId]);

  const getEvents = useCallback(() => {
    return sharedStore.events.get(normalizedId) || [];
  }, [normalizedId]);

  const getOdds = useCallback(() => {
    return sharedStore.odds.get(normalizedId);
  }, [normalizedId]);

  return {
    getScore,
    getEvents,
    getOdds,
    updateCounter, // Component update when updateCounter change
  };
}
